import * as React from "react";
import { AllIconNames, RenderLayer } from "react-ui";

import { ICandyApiDraftClient } from "../../../../../../Common/CandyApiClient/ICandyApiClient";
import { useKeyboardShortcut } from "../../../Commons/Keyboard/UseKeyboardShortcut";
import { Icon } from "../../../Icons/Icon";
import { ThemeVariant } from "../../ThemeProvider/ThemeProvider";

import {
    BigRoundedIcon,
    DebugActionItem,
    DebugActionItemCaptionAnimationContainer,
    DebugActionItemCaptionAnimationTarget,
    DebugActionItemCaptionContainer,
    DebugActionItemHotkey,
    DebugActionItemTitle,
    DebugActionItemTitleContainer,
    DropUpMenu,
    Root,
    SmallRoundedIcon,
} from "./DebugToolsHint.styles";

interface DebugToolsHintProps {
    formDebugMode: boolean;
    showDebugToolsHint: boolean;
    candyApiDraftClient: ICandyApiDraftClient;
    onShowControlDebugInfo?: () => void;
    onToggleDebugMode?: () => void;
    onToggleReadonlyMode?: () => void;
    onShowSearchPathModal?: () => void;
    onShowFormDebugInfo?: () => void;
    onShowFakeErrorLogsSetter?: () => void;
    onShowShowSetInnerModal?: () => void;
    onSetFakeRequisites?: () => void;
    onRefreshWithNormalization?: () => void;
    onToggleTheme?: (theme: ThemeVariant) => void;
    onSendForm?: () => void;
    onToggleDisabledClean?: () => void;
    onToggleIndividual?: () => void;
    onToggleScenario?: () => void;
    onDownloadFuf?: () => void;
    isReadOnly?: boolean;
    isIndividual?: boolean;
    isDisabledCleanForm?: boolean;
    isDebug?: boolean;
}

interface DebugToolsItemProps {
    onClick: () => void;
    hotkeysCaption: string[];
    title: React.ReactNode;
    icon: React.ReactElement | AllIconNames;
    href?: string;
    target?: "_blank";
    disabled?: boolean;
}

function DebugToolsItem(props: DebugToolsItemProps) {
    return (
        <DebugActionItem onClick={props.onClick} href={props.href} target={props.target}>
            <DebugActionItemCaptionAnimationContainer>
                <DebugActionItemCaptionAnimationTarget>
                    <DebugActionItemCaptionContainer>
                        <DebugActionItemHotkey>
                            {props.hotkeysCaption.map(caption => (
                                <React.Fragment key={caption}>
                                    <span>{caption}</span>
                                    <br />
                                </React.Fragment>
                            ))}
                        </DebugActionItemHotkey>
                        <DebugActionItemTitleContainer>
                            <DebugActionItemTitle>
                                {props.title}
                                {props.href != undefined && " "}
                                {props.href != undefined && <Icon name="ArrowShapeDRadiusUpRightIcon" />}
                            </DebugActionItemTitle>
                        </DebugActionItemTitleContainer>
                    </DebugActionItemCaptionContainer>
                </DebugActionItemCaptionAnimationTarget>
            </DebugActionItemCaptionAnimationContainer>
            <SmallRoundedIcon>
                {typeof props.icon === "string" ? <Icon name={props.icon} size={16} /> : props.icon}
            </SmallRoundedIcon>
        </DebugActionItem>
    );
}

export function DebugToolsHint(props: DebugToolsHintProps): JSX.Element {
    const [opened, setOpened] = React.useState(false);
    const hasSupportButton = React.useMemo(() => {
        try {
            return (
                document.body.querySelector("[aria-label='Открыть Центр Поддержки']") != undefined ||
                document.body.querySelector("[data-tid='support-center-container']") != undefined ||
                document.body.querySelector("[data-tid='support-center-button']") != undefined ||
                document.body.querySelector("[class^=SupportButton]") != undefined
            );
        } catch {
            return false;
        }
    }, []);

    useKeyboardShortcut("Ctrl+Alt+B", () => {
        if (props.formDebugMode) {
            props.onShowControlDebugInfo?.();
        }
    });
    useKeyboardShortcut("Ctrl+Alt+R", () => {
        if (props.formDebugMode) {
            props.onToggleReadonlyMode?.();
        }
    });

    useKeyboardShortcut("Ctrl+Alt+P", () => {
        if (props.formDebugMode) {
            props.onShowSearchPathModal?.();
        }
    });

    useKeyboardShortcut("Ctrl+Alt+H", () => {
        setOpened(true);
    });

    useKeyboardShortcut("Ctrl+Alt+L", () => {
        if (props.formDebugMode) {
            props.onShowFakeErrorLogsSetter?.();
        }
    });

    useKeyboardShortcut("Ctrl+Alt+E", () => {
        if (props.formDebugMode) {
            window.open(props.candyApiDraftClient.getHrefToInnerEditor(), "_blank");
        }
    });

    useKeyboardShortcut("Ctrl+Alt+F12", () => {
        props.onShowFormDebugInfo?.();
    });

    useKeyboardShortcut("Ctrl+Shift+F12", () => {
        props.onShowFormDebugInfo?.();
    });

    useKeyboardShortcut("Ctrl+Shift+F5", () => {
        props.onRefreshWithNormalization?.();
    });

    return (
        <RenderLayer onClickOutside={() => setOpened(false)}>
            <Root doNotHideSupportButton={hasSupportButton} opened={opened}>
                {(props.showDebugToolsHint || opened) && (
                    <BigRoundedIcon opened={opened} onClick={() => setOpened(!opened)}>
                        {opened ? <Icon name={"XIcon"} size={24} /> : <Icon name={"BagBriefcaseIcon"} size={24} />}
                    </BigRoundedIcon>
                )}
                <DropUpMenu opened={opened}>
                    <DebugToolsItem
                        icon="NetDownloadIcon"
                        onClick={() => {
                            setOpened(false);
                            props.onDownloadFuf?.();
                        }}
                        hotkeysCaption={[]}
                        title={"Скачать ФУФ"}
                    />
                    <DebugToolsItem
                        icon="CalculatorIcon"
                        onClick={() => {
                            setOpened(false);
                            props.onToggleScenario?.();
                        }}
                        hotkeysCaption={[]}
                        title={"Переключить scenario"}
                    />
                    <DebugToolsItem
                        icon="People1BossIcon"
                        onClick={() => {
                            setOpened(false);
                            props.onToggleIndividual?.();
                        }}
                        hotkeysCaption={[]}
                        title={`Включить isIndividual=${!props.isIndividual}`}
                    />
                    <DebugToolsItem
                        icon="BlockIcon"
                        onClick={() => {
                            setOpened(false);
                            props.onToggleDisabledClean?.();
                        }}
                        hotkeysCaption={[]}
                        title={`Включить isDisabledCleanForm=${!props.isDisabledCleanForm}`}
                    />
                    <DebugToolsItem
                        icon="SendPaperplaneDiagIcon"
                        onClick={() => {
                            props.onSendForm?.();
                        }}
                        hotkeysCaption={[]}
                        title={"Отправка без проверок в редакторе"}
                    />
                    <DebugToolsItem
                        icon="EyeOpenIcon"
                        onClick={() => {
                            setOpened(false);
                            props.onToggleTheme?.(ThemeVariant.Dark);
                        }}
                        hotkeysCaption={[]}
                        title={"Тёмная тема"}
                    />
                    <DebugToolsItem
                        icon="FileFormatDocIcon"
                        onClick={() => {
                            setOpened(false);
                            props.onToggleTheme?.(ThemeVariant.Diadoc);
                        }}
                        hotkeysCaption={[]}
                        title={"Тема Диадока"}
                    />
                    <DebugToolsItem
                        icon="FileFormatDocIcon"
                        onClick={() => {
                            setOpened(false);
                            props.onToggleTheme?.(ThemeVariant.Logistics);
                        }}
                        hotkeysCaption={[]}
                        title={"Тема Логистики"}
                    />
                    <DebugToolsItem
                        icon="DocsTextIcon"
                        title={"Фейковые реквизиты"}
                        hotkeysCaption={[]}
                        onClick={() => {
                            setOpened(false);
                            props.onSetFakeRequisites?.();
                        }}
                    />
                    <DebugToolsItem
                        icon="TechPcLaptopIcon"
                        onClick={() => {
                            setOpened(false);
                            props.onToggleDebugMode?.();
                        }}
                        hotkeysCaption={["Ctrl + Alt + D"]}
                        title={`Включить debug=${!props.isDebug}`}
                    />
                    <DebugToolsItem
                        icon="TechPcLaptopIcon"
                        onClick={() => {
                            setOpened(false);
                            props.onToggleReadonlyMode?.();
                        }}
                        hotkeysCaption={["Ctrl + Alt + R"]}
                        title={`Включить ReadOnly=${!props.isReadOnly}`}
                    />
                    <DebugToolsItem
                        icon="ArrowFigureASquareDownIcon"
                        onClick={() => setOpened(false)}
                        title={"Скачать innerFuf"}
                        target={"_blank"}
                        href={props.candyApiDraftClient.getHrefToDownloadInnerFuf()}
                        hotkeysCaption={[]}
                    />
                    <DebugToolsItem
                        icon="People1Icon"
                        onClick={() => setOpened(false)}
                        title={"Скачать formClientInfo"}
                        target={"_blank"}
                        href={props.candyApiDraftClient.getHrefToDownloadFormsClientInfo()}
                        hotkeysCaption={[]}
                    />
                    <DebugToolsItem
                        icon="InfoSquareIcon"
                        title={"Настройки и инфо о форме"}
                        hotkeysCaption={["Ctrl + Shift + F12", "Ctrl + Alt + F12"]}
                        onClick={() => {
                            setOpened(false);
                            props.onShowFormDebugInfo?.();
                        }}
                    />
                    <DebugToolsItem
                        icon="DocArrowConvertIcon"
                        title={"Отобразить пути ФУФ-а"}
                        hotkeysCaption={["Ctrl + Alt + B"]}
                        onClick={() => {
                            setOpened(false);
                            props.onShowControlDebugInfo?.();
                        }}
                    />
                    <DebugToolsItem
                        icon="SearchLoupeIcon"
                        title={"Поиск контролов по путям"}
                        hotkeysCaption={["Ctrl + Alt + P"]}
                        onClick={() => {
                            setOpened(false);
                            props.onShowSearchPathModal?.();
                        }}
                    />
                    <DebugToolsItem
                        onClick={() => setOpened(false)}
                        target={"_blank"}
                        href={props.candyApiDraftClient.getHrefToInnerEditor()}
                        icon="ToolPencilLineIcon"
                        title={"Открыть редактор иннера"}
                        hotkeysCaption={["Ctrl + Alt + E"]}
                    />
                    <DebugToolsItem
                        onClick={() => setOpened(false)}
                        target={"_blank"}
                        href={props.candyApiDraftClient.getHrefToConvertationLog()}
                        icon="ArrowParallelHIcon"
                        title={"Показать конвертации"}
                        hotkeysCaption={[]}
                    />
                    <DebugToolsItem
                        icon="DocXIcon"
                        title={"Фейковый протокол контроля"}
                        hotkeysCaption={["Ctrl + Alt + L"]}
                        onClick={() => {
                            setOpened(false);
                            props.onShowFakeErrorLogsSetter?.();
                        }}
                    />
                    <DebugToolsItem
                        icon="ArrowUiShareAImportIcon"
                        title={"Установить inner"}
                        hotkeysCaption={[]}
                        onClick={() => {
                            setOpened(false);
                            props.onShowShowSetInnerModal?.();
                        }}
                    />
                    <DebugToolsItem
                        icon="ArrowRoundTimeForwardIcon"
                        title={"Перезагрузить с нормализацией"}
                        hotkeysCaption={["Ctrl + Shift + F5"]}
                        onClick={() => {
                            setOpened(false);
                            props.onRefreshWithNormalization?.();
                        }}
                    />
                </DropUpMenu>
            </Root>
        </RenderLayer>
    );
}
